import { billStatusLabel } from '@/utils/billingLabels';
import { toDateOnly } from '@/utils/dateOnly';

/**
 * Pure rules behind the student billing page: which bill is payable now,
 * what goes in the checkout, and the badge each bill wears. Kept out of the
 * view so they can be tested without mounting it.
 */

export interface PayableFee {
  id: string;
  balance: number;
}

// @allow-handwritten generic constraint for the pure selection rules; any bill-shaped row (query result or test fixture) satisfies it
export interface PayableBill<F extends PayableFee = PayableFee> {
  id: string;
  balance: number;
  amount_paid?: number | null;
  due_date?: string | null;
  created_at?: string | null;
  billDetails: F[];
}

export interface SelectedFee<B extends PayableBill> {
  detail: B['billDetails'][number];
  bill: B;
}

/** The calendar day of a date-only ('2026-10-06') or datetime ('2026-10-06 09:00:00') value. */
export function calendarDay(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

/**
 * A bill is overdue only once its due DAY has passed. Parsing '2026-10-06'
 * with new Date() gives UTC midnight, which in Lagos marked a bill due today
 * as overdue from 01:00.
 */
export function isPastDue(dueDate: string | null | undefined, now: Date = new Date()): boolean {
  const due = calendarDay(dueDate);
  if (!due) return false;

  return toDateOnly(now) > due;
}

/** A date-only value as "6 Oct 2026", read as that calendar day (no UTC shift). */
export function formatDay(value: string | null | undefined): string {
  const day = calendarDay(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const [year, month, date] = day.split('-').map(Number);

  return new Date(year!, month! - 1, date!).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
}

export type BadgeSeverity = 'success' | 'warn' | 'danger';

export function payerBillBadge(
  bill: Pick<PayableBill, 'balance' | 'amount_paid' | 'due_date'>,
  now: Date = new Date(),
): { label: string; severity: BadgeSeverity; icon?: string } | null {
  if (bill.balance <= 0) return { label: billStatusLabel('PAID', 'payer'), severity: 'success', icon: 'pi pi-check' };
  if ((bill.amount_paid ?? 0) > 0) return { label: billStatusLabel('PARTIALLY_PAID', 'payer'), severity: 'warn' };
  if (isPastDue(bill.due_date, now)) return { label: billStatusLabel('OVERDUE', 'payer'), severity: 'danger' };

  return null;
}

function orderKey(bill: Pick<PayableBill, 'due_date' | 'created_at'>): string {
  return bill.due_date || bill.created_at || '';
}

/**
 * Older bills must be paid first: a bill is payable only when every older bill
 * with a balance has all of its unpaid fees in the checkout too.
 */
export function isBillPayable<B extends PayableBill>(
  bill: B,
  bills: B[],
  isSelected: (feeId: string) => boolean,
): boolean {
  if (bill.balance <= 0) return false;
  const key = orderKey(bill);

  return bills.every((other) => {
    if (other.id === bill.id || orderKey(other) >= key || other.balance <= 0) return true;

    return other.billDetails.every((fee) => fee.balance <= 0 || isSelected(fee.id));
  });
}

/**
 * Re-point the checkout at freshly loaded bills: fees that are now paid or gone
 * drop out, balances update, and fees whose bill became locked (an older bill's
 * fee was taken out) are removed — repeated until nothing else locks.
 */
export function reconcileSelection<B extends PayableBill>(selected: SelectedFee<B>[], bills: B[]): SelectedFee<B>[] {
  let current: SelectedFee<B>[] = [];
  for (const item of selected) {
    const bill = bills.find((b) => b.id === item.bill.id);
    const detail = bill?.billDetails.find((fee) => fee.id === item.detail.id);
    if (bill && detail && detail.balance > 0) current.push({ bill, detail });
  }

  for (;;) {
    const ids = new Set(current.map((item) => item.detail.id));
    const next = current.filter((item) => isBillPayable(item.bill, bills, (id) => ids.has(id)));
    if (next.length === current.length) return next;
    current = next;
  }
}

/**
 * Everything needed to pay one bill: its unpaid fees plus the unpaid fees of
 * every older bill (which must be paid first). Empty when the bill is paid.
 */
export function selectionForBill<B extends PayableBill>(billId: string, bills: B[]): SelectedFee<B>[] {
  const target = bills.find((bill) => bill.id === billId);
  if (!target || target.balance <= 0) return [];
  const key = orderKey(target);
  const older = bills.filter((bill) => bill.id !== target.id && bill.balance > 0 && orderKey(bill) < key);

  return [...older, target].flatMap((bill) => bill.billDetails
    .filter((fee) => fee.balance > 0)
    .map((detail) => ({ detail, bill })));
}

/** Why a Pay button is disabled, in words, or '' when it can be pressed. */
export function payBlockReason(options: { provider: string; isInterswitch: boolean; method: string }): string {
  if (!options.provider) return 'Choose how to pay first.';
  if (options.isInterswitch && !options.method) return 'Choose card or bank transfer above.';

  return '';
}
